import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import {
  Grid,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Box,
} from '@mui/material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import {
  IStudentSubjectAssessment,
  IStudentSubjectScoreSummary,
  IStudentSubjectBasedStudentAssessmentListItem,
  IScore,
  IStudentBasedAssessment,
} from 'src/apis/students/studentsAssessment.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useStudentAssessmentState,
  useStudentAssessmentActions,
} from '../../contexts/studentAssessment.context';
import { useStudentListState } from '../../contexts/studentList.context';
import { TPrimaryDetails } from 'src/apis/users/users.interfaces';
import { zNumberFromInput, zOptionalNumberFromInput } from 'src/utils/zodFormHelpers';

type StudentFormData = {
  isExpected: 'yes' | 'no';
  obtainedScore?: number;
};

type AssessmentFormData = {
  summary: {
    title: string;
    description: string;
    passingScore: number;
    totalScore: number;
  };
  students: Record<string, StudentFormData>;
};

const getAssessmentFormSchema = (
  translateText: (key: string) => string
): ZodType<AssessmentFormData> =>
  z.object({
    summary: z.object({
      title: z.string().min(1, translateText('common.fieldRequired')),
      description: z.string().min(1, translateText('common.fieldRequired')),
      passingScore: zNumberFromInput({
        min: 0,
        max: 100,
        message: 'Passing score must be between 0 and 100',
      }),
      totalScore: zNumberFromInput({
        min: 1,
        message: translateText('common.fieldRequired'),
      }),
    }),
    students: z.record(
      z.object({
        isExpected: z.enum(['yes', 'no']),
        obtainedScore: zOptionalNumberFromInput(),
      })
    ),
  }) as ZodType<AssessmentFormData>;

const AddEditAssessmentModal: FC = () => {
  const { studentAssessmentAppState } = useStudentAssessmentState();
  const { closeModal, handleSubmitAssessment } = useStudentAssessmentActions();
  const { studentList } = useStudentListState();

  const { translateText } = useTranslation();
  const assessmentFormSchema = getAssessmentFormSchema(translateText);
  const assessment = studentAssessmentAppState.selectedAssessmentForEdit;
  const selectedAssessmentId = studentAssessmentAppState.selectedAssessmentId;
  const isOpen = studentAssessmentAppState.isModalOpen;
  const isLoading = studentAssessmentAppState.isMutationLoading;
  const errorMessage = studentAssessmentAppState.mutationErrorMessage || '';

  const existingAssessmentsMap = useMemo(() => {
    if (!assessment?.assessments) return {};
    return assessment.assessments.reduce(
      (acc, item) => {
        acc[item.assessment.studentId] = item.assessment;
        return acc;
      },
      {} as Record<string, IStudentBasedAssessment>
    );
  }, [assessment]);

  const defaultValues: AssessmentFormData = {
    summary: {
      title: '',
      description: '',
      passingScore: 50,
      totalScore: 100,
    },
    students: {},
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<AssessmentFormData>({
    resolver: zodResolver(assessmentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const watchedStudents = watch('students');
  const [filterMode, setFilterMode] = useState<'all' | 'expected'>('all');

  const filteredStudentList = useMemo(() => {
    if (filterMode === 'all') {
      return studentList;
    }
    return studentList.filter((student) => {
      const isExpected = watchedStudents?.[student.id]?.isExpected || 'yes';
      return isExpected === 'yes';
    });
  }, [studentList, filterMode, watchedStudents]);

  useEffect(() => {
    if (isOpen) {
      const studentsData: Record<string, StudentFormData> = {};

      studentList.forEach((student) => {
        const existingAssessment = existingAssessmentsMap[student.id];
        // In edit mode: if student has assessment, they are expected; otherwise not expected
        // In add mode: all students are expected by default
        const isExpected = assessment
          ? existingAssessment
            ? 'yes'
            : 'no'
          : 'yes';
        studentsData[student.id] = {
          isExpected,
          obtainedScore: existingAssessment?.scores?.obtainedScore,
        };
      });

      reset({
        summary: {
          title: assessment?.summary?.title || '',
          description: assessment?.summary?.description || '',
          passingScore: assessment?.summary?.passingScore || 50,
          totalScore: assessment?.summary?.totalScore || 100,
        },
        students: studentsData,
      });
    }
  }, [assessment, isOpen, reset, studentList, existingAssessmentsMap]);

  const onSubmitForm = (data: AssessmentFormData) => {
    const totalScore = data.summary.totalScore;
    const passingScore = data.summary.passingScore;
    const currentDate = new Date().toISOString().split('T')[0];

    const assessments: IStudentSubjectBasedStudentAssessmentListItem[] = [];
    let expectedStudents = 0;
    let actualStudents = 0;
    let passedStudents = 0;
    let failedStudents = 0;
    let totalObtainedScore = 0;

    studentList.forEach((student) => {
      const studentData = data.students[student.id];
      if (!studentData) return;

      const isExpected = studentData.isExpected === 'yes';
      if (isExpected) {
        expectedStudents++;
      }

      if (
        studentData.obtainedScore !== undefined &&
        studentData.obtainedScore !== null
      ) {
        actualStudents++;
        totalObtainedScore += studentData.obtainedScore;

        const percentage =
          totalScore > 0 ? (studentData.obtainedScore / totalScore) * 100 : 0;
        const passed = studentData.obtainedScore >= passingScore;

        if (passed) {
          passedStudents++;
        } else {
          failedStudents++;
        }

        const studentPrimaryDetails: TPrimaryDetails = {
          id: student.id,
          firstName: student.basicDetails.firstName,
          middleName: student.basicDetails.middleName,
          lastName: student.basicDetails.lastName,
          gender: student.basicDetails.gender,
        };

        const score: IScore = {
          id: existingAssessmentsMap[student.id]?.scores?.id || '',
          subject: '',
          scoreType: '',
          maxScore: totalScore,
          obtainedScore: studentData.obtainedScore,
          percentage,
          date: currentDate,
        };

        const studentAssessment: IStudentBasedAssessment = {
          id: existingAssessmentsMap[student.id]?.id || '',
          studentId: student.id,
          studentDetails: studentPrimaryDetails,
          scores: score,
          action: existingAssessmentsMap[student.id]?.action || {
            created: { userId: '', timestamp: Date.now() },
          },
        };

        assessments.push({
          id: existingAssessmentsMap[student.id]
            ? assessment?.assessments?.find(
              (a) => a.assessment.studentId === student.id
            )?.id || ''
            : '',
          assessment: studentAssessment,
        });
      }
    });

    const totalPercentage =
      actualStudents > 0
        ? (totalObtainedScore / (actualStudents * totalScore)) * 100
        : 0;

    const summary: IStudentSubjectScoreSummary = {
      totalScore,
      totalPercentage,
      expectedStudents,
      actualStudents,
      passedStudents,
      failedStudents,
      passingScore,
      title: data.summary.title,
      description: data.summary.description,
    };

    const assessmentData: IStudentSubjectAssessment = {
      id: assessment?.id || '',
      summary,
      assessments,
      action: assessment?.action || {
        created: { userId: '', timestamp: Date.now() },
      },
    };

    handleSubmitAssessment(assessmentData);
  };

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={closeModal}
      title={
        assessment
          ? translateText('students.assessments.editAssessment') ||
          'Edit Assessment'
          : translateText('students.assessments.addAssessment') ||
          'Add Assessment'
      }
      drawerWidth="50vw"
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={
        assessment
          ? translateText('common.update')
          : translateText('common.save')
      }
      footerNegativeAction={closeModal}
      footerNegativeActionText={translateText('common.cancel')}
      footerMessage={
        errorMessage ? (
          <Typography
            color="error"
            variant="body2"
            data-testid="assessment-error-message"
          >
            {errorMessage}
          </Typography>
        ) : null
      }
      isLoading={isLoading}
      data-testid="add-edit-assessment-modal"
    >
      <form
        onSubmit={handleSubmit(onSubmitForm)}
        style={{ width: '100%' }}
        data-testid="assessment-form"
      >
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          padding={{ xs: 1, sm: 2 }}
          data-testid="assessment-form-container"
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              gutterBottom
              data-testid="assessment-form-title"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {translateText('students.assessments.assessmentDetails') ||
                'Assessment Details'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              name="summary.title"
              id="assessment-title"
              label={translateText('common.title') || 'Title'}
              errorMessage={errors.summary?.title?.message}
              isGridItem={false}
              dataTestId="assessment-title-field"
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              name="summary.description"
              id="assessment-description"
              label={translateText('common.description') || 'Description'}
              errorMessage={errors.summary?.description?.message}
              isGridItem={false}
              multiline
              rows={3}
              dataTestId="assessment-description-field"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ControlledTextField
              control={control}
              name="summary.totalScore"
              id="assessment-total-score"
              label={
                translateText('students.assessments.columns.totalScore') ||
                'Total Score'
              }
              errorMessage={errors.summary?.totalScore?.message}
              isGridItem={false}
              type="number"
              dataTestId="assessment-total-score-field"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ControlledTextField
              control={control}
              name="summary.passingScore"
              id="assessment-passing-score"
              label={
                translateText('students.assessments.columns.passingScore') ||
                'Passing Score'
              }
              errorMessage={errors.summary?.passingScore?.message}
              isGridItem={false}
              type="number"
              dataTestId="assessment-passing-score-field"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: { xs: 1.5, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 0 },
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                data-testid="students-section-title"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  mb: { xs: 0, sm: 0 },
                }}
              >
                {translateText('students.assessments.students') || 'Students'}
              </Typography>

              {selectedAssessmentId ? (
                <ToggleButtonGroup
                  value={filterMode}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setFilterMode(newValue);
                    }
                  }}
                  size="small"
                  data-testid="student-filter-toggle"
                >
                  <ToggleButton value="all" data-testid="filter-show-all">
                    {translateText('students.assessments.showAll') ||
                      'Show All'}
                  </ToggleButton>
                  <ToggleButton
                    value="expected"
                    data-testid="filter-show-expected"
                  >
                    {translateText('students.assessments.showExpected') ||
                      'Show Expected'}
                  </ToggleButton>
                </ToggleButtonGroup>
              ) : null}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }} data-testid="students-list-container">
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={{ xs: 1.5, sm: 2 }}
                  sx={{
                    fontWeight: 'bold',
                    mb: { xs: 0.5, sm: 1 },
                    display: { xs: 'none', sm: 'flex' },
                  }}
                >
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle2"
                      data-testid="student-name-header"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {translateText(
                        'students.assessments.columns.studentName'
                      ) || 'Student Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle2"
                      data-testid="expected-student-header"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {translateText('students.assessments.expectedStudent') ||
                        'Expected Student'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle2"
                      data-testid="obtained-score-header"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {translateText(
                        'students.assessments.columns.obtainedScore'
                      ) || 'Obtained Score'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {filteredStudentList.map((student) => {
                const studentName =
                  `${student.basicDetails.firstName} ${student.basicDetails.middleName || ''} ${student.basicDetails.lastName}`.trim();
                const isExpected =
                  watchedStudents?.[student.id]?.isExpected || 'yes';
                const isScoreDisabled = isExpected === 'no';

                return (
                  <Grid
                    item
                    xs={12}
                    key={student.id}
                    data-testid={`student-row-${student.id}`}
                    sx={{
                      mb: { xs: 2, sm: 0 },
                      pb: { xs: 2, sm: 0 },
                      borderBottom: { xs: '1px solid', sm: 'none' },
                      borderColor: { xs: 'divider', sm: 'transparent' },
                    }}
                  >
                    <Grid
                      container
                      spacing={{ xs: 1.5, sm: 2 }}
                      alignItems="center"
                      sx={{
                        flexDirection: { xs: 'column', sm: 'row' },
                      }}
                    >
                      <Grid item xs={12} sm={4}>
                        <Typography
                          variant="body2"
                          data-testid={`student-name-${student.id}`}
                          sx={{
                            fontSize: { xs: '0.875rem', sm: '0.875rem' },
                            fontWeight: { xs: 600, sm: 400 },
                            mb: { xs: 1, sm: 0 },
                          }}
                        >
                          {studentName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name={`students.${student.id}.isExpected`}
                          control={control}
                          render={({ field }) => (
                            <FormControl
                              component="fieldset"
                              size="small"
                              data-testid={`expected-student-control-${student.id}`}
                            >
                              <RadioGroup
                                {...field}
                                row={false}
                                sx={{
                                  flexDirection: { xs: 'row', sm: 'row' },
                                }}
                                value={field.value || 'yes'}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  // Clear obtainedScore when switching to 'no'
                                  if (e.target.value === 'no') {
                                    setValue(
                                      `students.${student.id}.obtainedScore`,
                                      undefined
                                    );
                                  }
                                }}
                              >
                                <FormControlLabel
                                  value="yes"
                                  control={
                                    <Radio
                                      size="small"
                                      data-testid={`expected-yes-${student.id}`}
                                    />
                                  }
                                  label={translateText('common.yes') || 'Yes'}
                                />
                                <FormControlLabel
                                  value="no"
                                  control={
                                    <Radio
                                      size="small"
                                      data-testid={`expected-no-${student.id}`}
                                    />
                                  }
                                  label={translateText('common.no') || 'No'}
                                />
                              </RadioGroup>
                            </FormControl>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ControlledTextField
                          control={control}
                          name={`students.${student.id}.obtainedScore`}
                          id={`student-score-${student.id}`}
                          label=""
                          errorMessage={
                            errors.students?.[student.id]?.obtainedScore
                              ?.message
                          }
                          isGridItem={false}
                          type="number"
                          dataTestId={`student-score-field-${student.id}`}
                          placeholder={
                            translateText(
                              'students.assessments.columns.obtainedScore'
                            ) || 'Score'
                          }
                          disabled={isScoreDisabled}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </StructuredModal>
  );
};

export default AddEditAssessmentModal;
